import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { BaseForm } from 'projects/admin/src/app/classes/base-form';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { DataScopeModel } from 'projects/core/src/lib/models/data-scope.model';

@Component({
  selector: 'app-data-scope-basedata',
  templateUrl: './data-scope-basedata.component.html',
  styleUrls: ['./data-scope-basedata.component.scss']
})
export class DataScopeBasedataComponent extends BaseForm implements OnInit {

  @Input() scope: DataScopeModel = null;
  @Output() setEdit = new EventEmitter<string>();

  public dimensionOptions: Array<{ key: string, label: string }> = [];

  public form = this.fb.group({
    name: new UntypedFormControl(''),
    dimensions: new UntypedFormControl([], [Validators.required]),
    title_de: new UntypedFormControl('', [Validators.required]),
    title_en: new UntypedFormControl('', [Validators.required]),
    description: new UntypedFormControl(''),
    active: new UntypedFormControl(false, [Validators.required]),
  });

  constructor(
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private api: ApiService,
    private transloco: TranslocoService
  ) {
    super();
  }

  ngOnInit(): void {
    this.refreshDimensionOptions();
    this.ui.setFocus();
    this.form.get('name').disable();
  }

  onSubmit(): void {
    this.ui.hideMessage();
    if (this.form.valid === true) {
      this.submitting = true;
      const payload: any = {
        title_de: this.form.get('title_de').value,
        title_en: this.form.get('title_en').value,
        description: this.form.get('description').value,
        active: this.form.get('active').value,
        dimensions: this.getSelectedDimensionKeys(),
      };
      this.api.saveModel('data-scopes', this.scope.id, payload).subscribe({
        next: (result: any) => {
          this.ui.toastSuccess(this.transloco.translate('common.messages.saved', { value: this.scope.name }), this.transloco.translate('common.saved'));
          this.scope.deserialize(result.data);
          this.stopEdit();
        },
        error: (error: any) => {
          this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
          FormHelper.setBackendErrors(this.form, error.fielderrors);
        }
      }).add(() => {
        this.submitting = false;
      });
    } else {
      this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
      FormHelper.markFormAsDirty(this.form);
    }
  }

  showDeactivateWarning(): boolean {
    if (this.scope === null) {
      return false;
    }
    if (this.inEdit === true) {
      return this.form.get('active').value === false;
    }
    return this.scope.active === false;
  }

  showEmptyDimensionWarning(): boolean {
    return this.relevantDimensionKeys().some((key: string) => this.entryCount(key) === 0);
  }

  showDimensionChangeWarning(): boolean {
    if (this.inEdit !== true || this.scope === null) {
      return false;
    }
    const current = this.getSelectedDimensionKeys().slice().sort().join('|');
    const saved = (this.scope.dimensions || []).slice().sort().join('|');
    return current !== saved;
  }

  getDimensionsLabel(): string {
    if (this.scope === null || !this.scope.dimensions || this.scope.dimensions.length === 0) {
      return '';
    }
    return this.scope.dimensions.map((dimension: string) => {
      if (dimension === 'template') {
        return this.transloco.translate('common.labels.procedures');
      }
      if (dimension === 'businessunit') {
        return this.transloco.translate('common.labels.businessunits');
      }
      return dimension;
    }).join(', ');
  }

  protected startEdit(): void {
    if (this.scope !== null) {
      this.refreshDimensionOptions();
      const selected = this.dimensionOptions.filter((option: { key: string }) => {
        return this.scope.dimensions && this.scope.dimensions.indexOf(option.key) !== -1;
      });
      this.form.patchValue({
        name: this.scope.name,
        dimensions: selected,
        title_de: (this.scope.title) ? this.scope.title['DE'] : null,
        title_en: (this.scope.title) ? this.scope.title['EN'] : null,
        description: this.scope.description,
        active: this.scope.active,
      });
    }
    this.setEdit.emit('basedata');
    super.startEdit();
    this.ui.setFormFocus();
  }

  private refreshDimensionOptions(): void {
    this.dimensionOptions = [
      { key: 'template', label: this.transloco.translate('common.labels.procedures') },
      { key: 'businessunit', label: this.transloco.translate('common.labels.businessunits') },
    ];
  }

  private relevantDimensionKeys(): Array<string> {
    if (this.inEdit === true) {
      return this.getSelectedDimensionKeys();
    }
    if (this.scope === null || !this.scope.dimensions) {
      return [];
    }
    return this.scope.dimensions;
  }

  private getSelectedDimensionKeys(): Array<string> {
    const selected = this.form.get('dimensions').value;
    if (!Array.isArray(selected)) {
      return [];
    }
    return selected.map((option: { key: string }) => option.key);
  }

  private dimensionBlock(key: string): any {
    if (this.scope === null) {
      return null;
    }
    if (key === 'template') {
      return this.scope.template;
    }
    if (key === 'businessunit') {
      return this.scope.businessunit;
    }
    return null;
  }

  private entryCount(key: string): number {
    if (this.scope !== null && this.scope.counts && this.scope.counts[key] !== undefined && this.scope.counts[key] !== null) {
      return this.scope.counts[key];
    }
    const block = this.dimensionBlock(key);
    if (block && Array.isArray(block.entries)) {
      return block.entries.length;
    }
    return 0;
  }

  protected stopEdit(): void {
    this.form.reset();
    this.setEdit.emit(null);
    super.stopEdit();
    this.ui.setFocus();
  }

}
